import axios, { AxiosError, AxiosResponse } from 'axios'
import { Observable } from 'rxjs'

import { pause } from 'utils'

class BadResponseError extends Error {}

export const isAxiosError = (error): error is AxiosError =>
  !!error?.isAxiosError

export const requestWithMinWait = (promise, wait) => {
  return Promise.all([
    promise,
    new Promise((resolve) => window.setTimeout(resolve, wait)),
  ]).then((values) => values[0])
}

const checkStatus = (response) => {
  if (response.status >= 500) {
    throw new BadResponseError()
  }

  return response
}

export const retry = async (
  request,
  retries = 3,
  iteration = 0,
  strategy = (x) => 2 ** x
) => {
  try {
    const response = await request()
    return checkStatus(response)
  } catch (err) {
    if (iteration < retries) {
      await pause(strategy(iteration) * 1000)
      return retry(request, retries, iteration + 1, strategy)
    }
    throw err
  }
}

export const mapResponse = <T = any>(response: AxiosResponse<T>) =>
  response.data

export const fromRequest = <T>(request: Promise<T>) =>
  new Observable<T>((subscriber) => {
    request
      .then((response) => {
        subscriber.next(response)
        subscriber.complete()
      })
      .catch((err: Error) => {
        console.error(err)
        subscriber.error(err)
        subscriber.complete()
      })
  })

export const createCancelTokenSource = () => axios.CancelToken.source()

export const cancelCatcher = (e) => {
  if (axios.isCancel(e)) {
    return Promise.reject({ canceled: true })
  }
  throw e
}
